import { ApiError } from "../api/client";
import {
  showQuotaPaywall,
  type QuotaScope,
  type QuotaCap,
} from "../stores/quotaPaywallStore";

/**
 * User-facing message for an error thrown by an AI-backed endpoint.
 *
 * A 429 with code `QUOTA_EXHAUSTED` means the tenant's AI token budget for
 * the period is used up, and a 429 with code `PROJECT_BUDGET_EXHAUSTED`
 * means that one project's monthly budget is (FLOW-850/FLOW-910) — each gets
 * its own explanatory copy; a 429 with any other code (e.g. `RATE_LIMITED`)
 * falls back to the server's message. A 503 from these endpoints means the
 * AI provider is unreachable, which is transient and worth phrasing as such;
 * everything else falls back to the server's own message.
 *
 * `surfaceQuotaExhausted` is called explicitly at user-initiated AI call
 * sites, never from background fetches — see FLOW-795 D2. Not every
 * `describeAiError(` call site is wired this way; wiring follows the
 * gesture, not the grep. Deliberately not wired: `ItemDetailPage`'s teaser
 * fetch, `ContextStalenessBanner`, `ImportCsvModal`'s mapping suggestion,
 * `EnrichmentPanel` (a degrade site that answers 200, never 429), and
 * `useDecisions`' `load` and `extract` — both fired by a `createEffect` on the
 * intake, not by any user action. Raising the paywall from one of these would
 * interrupt the user for a request they never asked for.
 */
export function describeAiError(err: unknown): string {
  const scope = quotaScope(err);
  if (scope === "project") {
    return "This AI feature is paused: this project has used its AI token budget for this month. Lighter AI features keep running; this one resumes when the budget resets.";
  }
  if (scope === "tenant") {
    return "This AI feature is paused: your organization has used its AI token budget for this period. Lighter AI features keep running; this one resumes when the budget resets.";
  }
  if (err instanceof ApiError) {
    return err.status === 503
      ? "AI service temporarily unavailable. Please try again shortly."
      : err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}

/**
 * Which budget a 429 was charged against: the tenant's caps
 * (`QUOTA_EXHAUSTED`, FLOW-789) or one project's monthly budget
 * (`PROJECT_BUDGET_EXHAUSTED`, FLOW-850/FLOW-910). `null` for anything else.
 */
export function quotaScope(err: unknown): QuotaScope | null {
  if (!(err instanceof ApiError) || err.status !== 429) return null;
  switch ((err.data as { code?: string } | null)?.code) {
    case "QUOTA_EXHAUSTED":
      return "tenant";
    case "PROJECT_BUDGET_EXHAUSTED":
      return "project";
    default:
      return null;
  }
}

/**
 * Which tenant window a 429 `QUOTA_EXHAUSTED` tripped (FLOW-928). `null` for
 * anything else, including `PROJECT_BUDGET_EXHAUSTED`, which has no cap to
 * name — the project ceiling is one figure, not a pair of windows.
 */
export function quotaCap(err: unknown): QuotaCap | null {
  if (!(err instanceof ApiError)) return null;
  const cap = (err.data as { cap?: string } | null)?.cap;
  return cap === "daily_burst" || cap === "monthly" ? cap : null;
}

/** Opens the paywall for a quota refusal. Returns true when it did, so the caller can skip its own toast (D7). */
export function surfaceQuotaExhausted(err: unknown): boolean {
  const scope = quotaScope(err);
  if (scope === null || !(err instanceof ApiError)) return false;
  showQuotaPaywall(err.retryAfterSecs, scope, quotaCap(err));
  return true;
}
